import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { TaskType } from './api/task-types/task-main.js';
import type {
  CopyDetectionResult,
  CopyFlagReason,
  PairwiseCopyScore,
  TaskSimilarityDetail,
} from './utils/copy-detection.js';

/** One task where two students' answers matched — the evidence behind a flagged pair. */
@Entity()
export class TaskSimilarityDetailDTO extends AdornisEntity {
  static override _class = 'TaskSimilarityDetailDTO';

  /** Identity for the client cache: unique within one pair's `perTask` list. */
  static override resolveID(data: any) {
    return data?.taskNumber;
  }

  @Field(() => String)
  taskNumber!: string;

  @Field(() => String)
  taskType!: TaskType;

  @Field(() => String)
  studentAAnswer: Maybe<string>;

  @Field(() => String)
  studentBAnswer: Maybe<string>;

  @Field(() => Float)
  rawSimilarity!: number;

  @Field(() => Float)
  surprisalWeight!: number;

  @Field(() => Float)
  wrongAnswerBoost!: number;

  @Field(() => Float)
  weightedScore!: number;

  @Field(() => Boolean)
  isWrongForBoth!: boolean;

  /** Absent on records computed before verbatim detection existed. */
  @Field(() => Boolean)
  isVerbatim: Maybe<boolean>;
}

/** One student pair's aggregate copy score for an exam, plus the per-task matches that produced it. */
@Entity()
export class PairwiseCopyScoreDTO extends AdornisEntity {
  static override _class = 'PairwiseCopyScoreDTO';

  static override resolveID(data: any) {
    return `${data?.studentIdA}|${data?.studentIdB}`;
  }

  @Field(() => String)
  studentIdA!: string;

  @Field(() => String)
  studentIdB!: string;

  @Field(() => Float)
  overallScore!: number;

  /** Tasks both students answered and were comparable on — the divisor of `overallScore`, not `perTask.length`. */
  @Field(() => Int)
  taskCount!: number;

  @Field(() => [TaskSimilarityDetailDTO])
  perTask!: TaskSimilarityDetailDTO[];

  @Field(() => Float)
  zScore!: number;

  @Field(() => Float)
  submissionSimilarity: Maybe<number>;

  @Field(() => Boolean)
  flagged!: boolean;

  /** `CopyFlagReason`; absent on records computed before flag reasons existed. */
  @Field(() => String)
  flagReason: Maybe<CopyFlagReason>;
}

/**
 * The stored copy-detection result for one exam. One document per exam, overwritten on every recompute —
 * see `copy-detection.job.ts`. Named `...Record` (not `CopyDetectionResult`, like its source type in
 * `utils/copy-detection.ts`) so a caller can import the pure computation result and the persisted entity
 * side by side without a name clash.
 */
@Entity()
export class CopyDetectionResultRecord extends MongoEntity {
  static override _class = 'CopyDetectionResultRecord';
  static override _collectionName = 'copy_detection_results';

  @Field(() => String)
  examId!: string;

  @Field(() => String)
  computedAt!: string;

  @Field(() => Int)
  submissionCount!: number;

  @Field(() => [PairwiseCopyScoreDTO])
  pairs!: PairwiseCopyScoreDTO[];

  @Field(() => [String])
  flaggedPairIds!: string[];

  @Field(() => Float)
  meanScore!: number;

  @Field(() => Float)
  stdDevScore!: number;

  /** Task numbers skipped because they have no comparable text answer (sketch/art/diagram/etc.) — shown in the
   *  UI so a teacher never mistakes "not flagged" for "checked and clean" on a task that was never checked. */
  @Field(() => [String])
  excludedTaskNumbers!: string[];

  /** Absent on records computed before this field existed. */
  @Field(() => [String])
  checkedTaskNumbers: Maybe<string[]>;
}

function toDetailDTO(detail: TaskSimilarityDetail): TaskSimilarityDetailDTO {
  return new TaskSimilarityDetailDTO(detail);
}

function toPairDTO(pair: PairwiseCopyScore): PairwiseCopyScoreDTO {
  return new PairwiseCopyScoreDTO({ ...pair, perTask: pair.perTask.map(toDetailDTO) });
}

/** Converts the pure computation result into the entity shape `CopyDetectionResultRecord` stores. */
export function toCopyDetectionResultRecord(result: CopyDetectionResult): CopyDetectionResultRecord {
  return new CopyDetectionResultRecord({ ...result, pairs: result.pairs.map(toPairDTO) });
}
